// Small, allocation-free animation helpers for the bean rig (§12.4): damped springs, the googly
// pupil, the expression rules and Dizzy tiers.
import { TUNE } from '@shared/constants';
import { BS } from '@shared/ids';
import type { BeanView } from '../../game/FrameView';
import { EXPR, type Expr } from './mouthAtlas';

/** A 1D damped spring toward `target`, integrated in fixed sub-steps (stable at any frame rate). */
export class Spring {
  x: number;
  v = 0;
  target: number;
  constructor(x: number, public k: number, public c: number) {
    this.x = x;
    this.target = x;
  }
  step(dt: number): number {
    let left = Math.min(dt, 0.1);
    while (left > 1e-6) {
      const h = Math.min(left, 1 / 240);
      this.v += (-this.k * (this.x - this.target) - this.c * this.v) * h;
      this.x += this.v * h;
      left -= h;
    }
    return this.x;
  }
  reset(x: number): void {
    this.x = x;
    this.target = x;
    this.v = 0;
  }
}

/**
 * The googly pupil (§12.4): a 2D damped spring (k 220, c 8) inside the eye disc, excited by head
 * acceleration (the pupil lags it) and by impacts. At the rim it bounces back: the "rattle".
 * Coordinates are eye-local meters; `limit` is how far the pupil center may travel.
 */
export class Pupil {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  /** Rim impacts so far (the rattle; tests and debug pages read it). */
  rimHits = 0;
  constructor(public k = 220, public c = 8, public limit = 0.055) {}
  /** `ax, ay`: eye-plane acceleration of the head (m/s²); the pupil feels the opposite. */
  step(dt: number, ax: number, ay: number, restX: number, restY: number): void {
    let left = Math.min(dt, 0.1);
    while (left > 1e-6) {
      const h = Math.min(left, 1 / 240);
      this.vx += (-this.k * (this.x - restX) - this.c * this.vx - ax * 0.01) * h;
      this.vy += (-this.k * (this.y - restY) - this.c * this.vy - ay * 0.01) * h;
      this.x += this.vx * h;
      this.y += this.vy * h;
      const r = len2(this.x, this.y);
      if (r > this.limit) {
        // Bounce off the rim with restitution 0.75 (the rattle: lively, several clacks per hit).
        const nx = this.x / r;
        const ny = this.y / r;
        this.x = nx * this.limit;
        this.y = ny * this.limit;
        const vn = this.vx * nx + this.vy * ny;
        if (vn > 0) {
          this.vx -= 1.75 * vn * nx;
          this.vy -= 1.75 * vn * ny;
          if (vn > 0.05) this.rimHits++;
        }
      }
      left -= h;
    }
  }
  /** An impact (§12.4: impulse 3 × kb, in eye-plane m/s scaled by the caller). */
  kick(ix: number, iy: number): void {
    this.vx += ix;
    this.vy += iy;
  }
  reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
  }
}

/** Transient expression causes, as view-time seconds until they end. */
export interface ExprTimers { smugUntil: number; ouchUntil: number; honkUntil: number }

export function createExprTimers(): ExprTimers {
  return { smugUntil: -1, ouchUntil: -1, honkUntil: -1 };
}

/**
 * "Flying" (§12.4): launched and helpless — a tumble in the air or still at launch speed, a yank,
 * the teacup spin, a peel slip.
 */
export function isFlying(b: BeanView): boolean {
  switch (b.state) {
    case BS.TUMBLE:
      return b.z > TUNE.bean.airborneZ || len2(b.vx, b.vy) >= TUNE.knock.tumbleThreshold;
    case BS.YANKED:
    case BS.SPUN:
    case BS.SLIP:
      return true;
    default:
      return false;
  }
}

/**
 * §12.4 expression rules, in priority order: ghost → sad; flying, falling or teetering (a ledge
 * hang counts as teetering) → O; grabbed → grimace; Dizzy ≥ 70 → zigzag; within 2 s after a
 * credited KO → smug; otherwise smile. §11.1 adds the transient O of a fresh hit or a honk, which
 * outranks zigzag and smug but never the peril rules.
 */
export function chooseExpression(b: BeanView, t: ExprTimers, now: number): Expr {
  if (b.ghost) return EXPR.SAD;
  if (b.state === BS.FALLING || b.state === BS.TEETER || b.state === BS.LEDGE || isFlying(b)) return EXPR.O;
  if (b.state === BS.GRABBED) return EXPR.GRIMACE;
  if (now < t.ouchUntil || now < t.honkUntil) return EXPR.O;
  if (b.dizzy >= TUNE.dizzy.flushAt) return EXPR.ZIGZAG;
  if (now < t.smugUntil) return EXPR.SMUG;
  return EXPR.SMILE;
}

/** Number of orbiting Dizzy stars (§11.1: 2 / 3 / 4 at 40 / 70 / 100). */
export function dizzyStars(dizzy: number): number {
  if (dizzy >= TUNE.dizzy.spiralAt) return 4;
  if (dizzy >= TUNE.dizzy.flushAt) return 3;
  if (dizzy >= TUNE.dizzy.starsAt) return 2;
  return 0;
}

/** √(x² + y²) without Math.hypot (V8 allocates an array per Math.hypot call). */
export function len2(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

/** √(x² + y² + z²) without Math.hypot. */
export function len3(x: number, y: number, z: number): number {
  return Math.sqrt(x * x + y * y + z * z);
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Frame-rate independent exponential approach factor for time constant `tau` seconds. */
export function approach(dt: number, tau: number): number {
  return tau <= 0 ? 1 : 1 - Math.exp(-dt / tau);
}

/** Smoothstep on [a, b]. */
export function smooth01(a: number, b: number, x: number): number {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}
